import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

import { SafeDatePipe } from '@agent-ds/shared/pipes/safe-date.pipe';
import { DynamicField } from '@agent-ds/shared/models';
import { MasterApiService } from '@agent-ds/shared/services';
import { Certificate, IndustryType, JobType } from '@agent-ds/shared/interfaces';
import { NULL_SELECTED_VALUE } from '@agent-ds/shared/constants';

enum DynamicFieldDisplayVariant {
  industry = 'industry', // 業種
  jobType = 'job-type', // 職種
  certificate = 'certificate', // 資格
  multilist = 'multilist',
  list = 'list',
  textarea = 'textarea',
  date = 'date',
  email = 'email',
  tel = 'tel',
  number = 'number',
  text = 'text',
}

@Component({
  selector: 'ag-counseling-memo-dynamic-field-input',
  templateUrl: './counseling-memo-dynamic-field-input.component.html',
  styleUrls: ['./counseling-memo-dynamic-field-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CounselingMemoDynamicFieldInputComponent),
      multi: true,
    },
  ],
})
export class CounselingMemoDynamicFieldInputComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy, ControlValueAccessor {
  public myForm: FormGroup = new FormGroup({
    value: new FormControl(null),
  });

  public get valueCtrl(): FormControl {
    return this.myForm.get('value') as FormControl;
  }

  @Input() fieldDefinition: DynamicField;

  /**
   * 後方互換：フォーム(CVA)で使っていない場合のみ外部から値を入れたい用途向け
   * ngModel / formControlName を使う場合は Angular 側から writeValue が呼ばれる
   */
  @Input() value: any;

  @Input() readonly: boolean = false;
  @Input() placeholder?: string;

  @Output() onFocused: EventEmitter<any> = new EventEmitter();
  @Output() onChanged: EventEmitter<any> = new EventEmitter();
  @Output() onBlured: EventEmitter<any> = new EventEmitter();

  public DynamicFieldDisplayVariant = DynamicFieldDisplayVariant;
  
  // 追加: multilist 候補リストの開閉状態
  public isMultiListOptionsOpen: boolean = false;

  private valueChangesSubscription?: Subscription;
  private fieldOptionMasterDataSubscription: Subscription = new Subscription();

  /** CVAが使われているかどうか（registerOnChange が呼ばれたら true） */
  private isControlValueAccessorActive: boolean = false;
  private certificatesMaster: Certificate[] = [];
  public certificateNameOptions: Array<{ value: number | string; label: string }> = [];
  public certificateGradeNameOptions: Array<{ value: number | string; label: string }> = [];
  private jobTypesMaster: JobType[] = [];
  public jobTypeCode1Options: Array<{ value: number | string; label: string }> = [];
  public jobTypeCode2Options: Array<{ value: number | string; label: string }> = [];
  private industryTypesMaster: IndustryType[] = [];
  public displayableLabelValue = '';

  /** ControlValueAccessor callbacks */
  private propagateChange: (value: any) => void = () => {};
  private propagateTouched: () => void = () => {};

  constructor(
    public readonly datePipe: SafeDatePipe,
    private masterService: MasterApiService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyReadonlyToControl();

    this.valueChangesSubscription = this.valueCtrl.valueChanges.subscribe((updatedValue) => {
      // ngModel / formControlName へ通知
      this.propagateChange(updatedValue);

      // 既存のOutputも維持
      this.onChanged.emit({ fieldName: this.fieldDefinition.fieldName, value: updatedValue });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly']) {
      this.applyReadonlyToControl();
    }

    // ngModel / formControl を使っていない場合は @Input value を反映
    if (!this.isControlValueAccessorActive && (changes['value'])) {
      this.writeValue(this.value);
    }

    if (changes['fieldDefinition'] && this.isControlValueAccessorActive) {
      setTimeout(() => {
        // ngModel時にもMasterが更新されるようマスター初期化処理を追加
        this.initFieldOptionMasterData();
        // 表示可能なラベル値を更新
        this.initDisplayableLabelValue();
        this.cdr.markForCheck();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription &&
      this.valueChangesSubscription.unsubscribe();
    
    this.fieldOptionMasterDataSubscription &&
      this.fieldOptionMasterDataSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
  }

  initDisplayableLabelValue(): void {
    switch (this.getDisplayVariant()) {
      case DynamicFieldDisplayVariant.jobType:
        this.displayableLabelValue = this.getJobTypeLabel();
        break;

      case DynamicFieldDisplayVariant.industry:
        this.displayableLabelValue = this.getIndustryLabel();
        break;

      case DynamicFieldDisplayVariant.certificate:
        this.displayableLabelValue = this.getCertificateLabel();
        break;
    }
  }

  // Master初期化処理
  initFieldOptionMasterData(): void {
    // すでに走っている購読があれば解除して張り直し（連続呼び出し対策）
    this.fieldOptionMasterDataSubscription.unsubscribe();
    this.fieldOptionMasterDataSubscription = new Subscription();

    if(this.isCertificate()) {
      const sub = this.masterService.getCertificates().subscribe({
        next: (certificates: Certificate[]) => {
          this.certificatesMaster = certificates || [];
          this.rebuildCertificateNameOptions();
          this.rebuildCertificateGradeNameOptions();
          this.cdr.markForCheck(); // ★再描画
        },
        error: (err) => {
          this.cdr.markForCheck();
          // this.loadingCustomer = false;
        },
        complete: () => {
          // this.loadingCustomer = false;
        }
      });
      this.fieldOptionMasterDataSubscription.add(sub);
    }
    if(this.isJobType()) {
      const sub = this.masterService.getJobTypes().subscribe({
        next: (jobTypes: JobType[]) => {
          this.jobTypesMaster = jobTypes || [];
          this.rebuildJobTypeCode1Options();
          this.rebuildJobTypeCode2Options();
          this.cdr.markForCheck(); // ★再描画
        },
        error: (err) => {
          this.cdr.markForCheck();
          // this.loadingCustomer = false;
        },
        complete: () => {
          // this.loadingCustomer = false;
        }
      });
      this.fieldOptionMasterDataSubscription.add(sub);
    }
    if(this.isIndustry()) {
      const sub = this.masterService.getIndustryTypes().subscribe({
        next: (industryTypes: IndustryType[]) => {
          this.industryTypesMaster = industryTypes || [];
          this.cdr.markForCheck(); // ★再描画
        },
        error: (err) => {
          this.cdr.markForCheck();
          // this.loadingCustomer = false;
        },
        complete: () => {
          // this.loadingCustomer = false;
        }
      });
      this.fieldOptionMasterDataSubscription.add(sub);
    }
  }

  // -----------------------
  // ControlValueAccessor
  // -----------------------

  writeValue(externalValue: any): void {
    const preparedValue = this.prepareInitialValue(this.fieldDefinition, externalValue);
    this.valueCtrl.patchValue(preparedValue, { emitEvent: false });
    setTimeout(() => {
      // ngModel時にもMasterが更新されるようマスター初期化処理を追加
      this.initFieldOptionMasterData();
      // 表示可能なラベル値を更新
      this.initDisplayableLabelValue();
      this.cdr.markForCheck();
    }, 100);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.isControlValueAccessorActive = true;
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // readonly input と setDisabledState の両方を尊重する
    const shouldDisable = isDisabled || this.readonly;
    if (shouldDisable) {
      this.valueCtrl.disable({ emitEvent: false });
    } else {
      this.valueCtrl.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  private applyReadonlyToControl(): void {
    if (this.readonly) {
      this.valueCtrl.disable({ emitEvent: false });
    } else {
      this.valueCtrl.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  // -----------------------
  // 表示種別（ngSwitch用）
  // -----------------------

  public getDisplayVariant(): DynamicFieldDisplayVariant {
    if (this.isIndustry()) return DynamicFieldDisplayVariant.industry;
    if (this.isJobType()) return DynamicFieldDisplayVariant.jobType;
    if (this.isCertificate()) return DynamicFieldDisplayVariant.certificate;
    if (this.isMultilist()) return DynamicFieldDisplayVariant.multilist;
    if (this.isList()) return DynamicFieldDisplayVariant.list;
    if (this.isTextarea()) return DynamicFieldDisplayVariant.textarea;
    if (this.isDate()) return DynamicFieldDisplayVariant.date;
    if (this.isEmail()) return DynamicFieldDisplayVariant.email;
    if (this.isTel()) return DynamicFieldDisplayVariant.tel;
    if (this.isNumber()) return DynamicFieldDisplayVariant.number;
    return DynamicFieldDisplayVariant.text;
  }

  // -----------------------
  // fieldType 判定（explicit naming）
  // -----------------------
  
  public isIndustry(): boolean {
    const fieldType = this.fieldDefinition.fieldType;
    return fieldType === 'industry';
  }
  public isJobType(): boolean {
    const fieldType = this.fieldDefinition.fieldType;
    return fieldType === 'job-type';
  }
  public isCertificate(): boolean {
    const fieldType = this.fieldDefinition.fieldType;
    return fieldType === 'certificate';
  }
  public isList(): boolean {
    const fieldType = this.fieldDefinition.fieldType;
    return fieldType === 'list';
  }

  public isMultilist(): boolean {
    const fieldType = this.fieldDefinition.fieldType;
    return fieldType === 'multi-list' || (typeof fieldType === 'string' && fieldType.startsWith('multi-'));
  }

  public isDate(): boolean {
    const fieldType = this.fieldDefinition.fieldType;
    const displayType = this.fieldDefinition.displayType;
    return fieldType === 'date' || displayType === 'date' || displayType === 'birthday';
  }

  public isEmail(): boolean {
    const fieldType = this.fieldDefinition.fieldType;
    const displayType = this.fieldDefinition.displayType;
    return fieldType === 'email' || displayType === 'email';
  }

  public isTel(): boolean {
    return this.fieldDefinition.fieldType === 'tel';
  }

  public isNumber(): boolean {
    return this.fieldDefinition.fieldType === 'number';
  }

  public isTextarea(): boolean {
    return this.fieldDefinition.fieldType === 'text' &&
      this.fieldDefinition.displayType === 'textarea';
  }

  public makeListGroupName(): string {
    const fieldName = this.fieldDefinition.fieldName || 'unknown';
    return `dynamic-field-input-list-${fieldName}`;
  }

  public makeListItemId(index: number): string {
    const fieldName = this.fieldDefinition.fieldName || 'unknown';
    return `dynamic-field-input-list-${fieldName}-${index}`;
  }

  /**
   * list の選択肢は validationStyle.options をそのまま「表示値＝値」として扱う
   * （例: ['はい', 'いいえ'] など）
   */
  public getListItems(): any[] {
    const rawItems = this.fieldDefinition.validationStyle.options || [];
    return Array.isArray(rawItems) ? rawItems : [];
  }

  public isListItemSelected(item: any): boolean {
    return this.valueCtrl.value === item;
  }

  public onSelectListItem(item: any, event?: MouseEvent): void {
    event && event.preventDefault();
    event && event.stopPropagation();

    const currentValue = this.valueCtrl.value;
    const nextValue = currentValue === item ? null : item;
    this.valueCtrl.setValue(nextValue);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.cdr.markForCheck();
  }

  /** readonly表示用（選択中の値をそのまま表示） */
  public getSelectedListItemLabel(): string {
    const currentValue = this.valueCtrl.value;
    return currentValue || '';
  }

  public shouldShowAddButton(): boolean {
    return this.isMultilist() && !this.isAllMultiListOptionSelected();
  }

  public checkIsArray(val:any): boolean {
    return Array.isArray(val);
  }
  public checkIsValueFilled(val:any): boolean {
    if(this.isCertificate()) {
      return !!this.getCertificateCode();
    }
    if(this.isJobType()) {
      return !!this.getJobTypeCode1();
    }
    if(this.isIndustry()) {
      return !!this.getIndustryCode();
    }
    return val != null && (Array.isArray(val) ? val.length > 0 : val);
  }

    // 追加: multilist の選択済み配列を安全に取得
  public getMultiListSelectedItems(): any[] {
    const currentValue = this.valueCtrl.value;
    return this.checkIsArray(currentValue) ? currentValue : [];
  }

  // 追加: multilist の候補を validationStyle.options から取得
  public getMultiListOptions(): any[] {
    const options = this.fieldDefinition.validationStyle.options || [];
    return this.checkIsArray(options) ? options : [];
  }

  // 追加: 候補がすでに選択済みか
  public isMultiListOptionSelected(option: any): boolean {
    return this.getMultiListSelectedItems().includes(option);
  }
  // 追加: 候補がすでに選択済みか
  public isAllMultiListOptionSelected(): boolean {
    return this.getMultiListOptions().every(op => (this.valueCtrl.value || []).includes(op));
  }


  // 追加: 候補を選択済み配列へ追加（重複は追加しない）
  public addMultiListOption(option: any): void {
    if (this.readonly) return;

    const selected = this.getMultiListSelectedItems();
    if (selected.includes(option)) {
      return;
    }

    const next = [...selected, option];
    if (this.getMultiListOptions().every(op => next.includes(op))) {
      this.isMultiListOptionsOpen = !this.isMultiListOptionsOpen;
    }
    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.cdr.markForCheck();
  }

  // 追加: toggleSelectOptionArea は候補リストを開閉
  public toggleSelectOptionArea(): void {
    if (this.readonly) return;

    this.isMultiListOptionsOpen = !this.isMultiListOptionsOpen;
  }

  // 修正: removeChip は readonly ガードを入れて安全に
  public removeChip(index: number): void {
    if (this.readonly) return;

    const current = this.getMultiListSelectedItems();
    const next = [...current];
    next.splice(index, 1);

    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.cdr.markForCheck();
  }

  // -----------------------
  // industry
  // valueCtrl.value: string(code) を想定
  // -----------------------
  public getIndustryCode(): string | null {
    const value = this.valueCtrl.value;
    return value != null ? String(value) : null;
  }
  public getIndustryLabel(): string {
    const code = this.getIndustryCode();
    if (!code) return '';

    const options = this.getIndustryOptions(); // [{value,label}...]
    const matched = options.find((option) => String(option.value) === String(code));
    return (matched && matched.label) || '';
  }

  public getIndustryOptions(): Array<{ value: number | string; label: string }> {
    const flattened = this.flattenIndustryTypes(this.industryTypesMaster || []);
    const options = flattened.map((it) => ({ value: String(it.code), label: it.name }));
    return this.createDropdownOptionsFrom(options);
  }

  private flattenIndustryTypes(items: IndustryType[]): IndustryType[] {
    const result: IndustryType[] = [];
    const walk = (list: IndustryType[]) => {
      for (const item of list || []) {
        result.push(item);
        if (item.industryTypes.length) walk(item.industryTypes);
      }
    };
    walk(items);
    return result;
  }

  public onIndustryCodeChange(code: string | null): void {
    this.valueCtrl.setValue(code || null);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.cdr.markForCheck();
  }

  // -----------------------
  // job-type
  // valueCtrl.value: { code1: number; code2: number } を想定
  // -----------------------
  public getJobTypeCode1(): number | null {
    const value = this.valueCtrl.value;
    if (!value || typeof value !== 'object') return null;
    return value.code1 != null ? Number(value.code1) : null;
  }

  public getJobTypeCode2(): number | null {
    const value = this.valueCtrl.value;
    if (!value || typeof value !== 'object') return null;
    return value.code2 != null ? Number(value.code2) : null;
  }

  public getJobTypeLabel(): string {
    const code1 = this.getJobTypeCode1();
    const code2 = this.getJobTypeCode2();
    if (!code1) return '';

    const parent = (this.jobTypesMaster || []).find(jt => Number(jt.code) === Number(code1));
    const parentLabel = (parent && parent.name) || '';

    if (!code2) return parentLabel;
    
    const children: JobType[] = (parent && Array.isArray(parent.jobTypes)) ? parent.jobTypes : [];
    const child = children.find(jt => Number(jt.code) === Number(code2));
    const childLabel = (child && child.name) || '';

    // childLabel が空なら親だけにする（undefinedを混ぜない）
    return childLabel ? `${parentLabel}\n${childLabel}` : parentLabel;
  }

  public getJobTypeParentOptions(): Array<{ value: number; label: string }> {
    return this.createDropdownOptionsFrom(
      (this.jobTypesMaster || []).map((jt) => ({
        value: Number(jt.code),
        label: jt.name,
      }))
    );
  }

  public getJobTypeChildOptions(): Array<{ value: number; label: string }> {
    const selectedParentCode = this.getJobTypeCode1();
    if (!selectedParentCode) {
      return [];
    }

    const parents: JobType[] = this.jobTypesMaster || [];
    const parent: JobType | undefined = parents.find((jt) => Number(jt.code) === Number(selectedParentCode));

    const children: JobType[] = Array.isArray(parent.jobTypes) ? parent.jobTypes : [];
    return this.createDropdownOptionsFrom(
      children.map(child => ({
        value: Number(child.code),
        label: child.name,
      }))
    );
  }
  private normalizeJobTypeValue(v: any): { code1: number | null; code2: number | null } {
    const code1 = v && v.code1 != null && v.code1 !== '' ? Number(v.code1) : null;
    const code2 = v && v.code2 != null && v.code2 !== '' ? Number(v.code2) : null;
    return { code1, code2 };
  }

  public onJobTypeCode1Change(code1: number | null): void {
    const current = this.normalizeJobTypeValue(this.valueCtrl.value);
    const nextCode1 = code1 != null ? Number(code1) : null;

    // ★ code1 が同じなら何もしない（＝code2 を落とさない）
    if (current.code1 === nextCode1) {
      return;
    }

    const next = { code1: String(nextCode1), code2: null };
    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();

    this.rebuildJobTypeCode2Options();
    // this.initDisplayableLabelValue();  // ラベルも同期更新（template がどちらを参照しても揺れにくくなる）
    this.cdr.markForCheck();
  }

  public onJobTypeCode2Change(code2: number | null): void {
    const current = this.normalizeJobTypeValue(this.valueCtrl.value);
    const nextCode2 = code2 != null ? Number(code2) : null;

    // ★ code2 が同じなら何もしない（無駄な揺れ防止）
    if (current.code2 === nextCode2) {
      return;
    }

    const next = { code1: String(current.code1), code2: String(nextCode2) };
    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();

    // this.initDisplayableLabelValue();
    this.cdr.markForCheck();
  }
  private rebuildJobTypeCode1Options(): void {
    this.jobTypeCode1Options = this.getJobTypeParentOptions(); // 既存関数を利用でOK
    this.cdr.markForCheck();
  }
  private rebuildJobTypeCode2Options(): void {
    this.jobTypeCode2Options = this.getJobTypeChildOptions(); // 既存関数を利用でOK
    this.cdr.markForCheck();
  }

  // -----------------------
  // certificate
  // valueCtrl.value:
  // {
  //   name: string, gradeName: string, code: number, gradeCode: number, issueYear: number, issueMonth: number
  // }
  // -----------------------
  public getCertificateCode(): string | null {
    const value = this.valueCtrl.value;
    return value.code != null ? String(value.code) : null;
  }

  public getCertificateGradeCode(): string | null {
    const value = this.valueCtrl.value;
    return value.gradeCode != null ? String(value.gradeCode) : null;
  }

  public getCertificateIssueYear(): number | null {
    const value = this.valueCtrl.value;
    return (value.issueYear || value.issueYear === 0) ? Number(value.issueYear) : null;
  }

  public getCertificateIssueMonth(): number | null {
    const value = this.valueCtrl.value;
    return (value.issueYear || value.issueYear === 0) ? Number(value.issueMonth) : null;
  }


  public getCertificateLabel(): string {
    const value = this.valueCtrl.value || {};

    const code = value.code != null ? String(value.code) : null;
    const gradeCode = value.gradeCode != null ? String(value.gradeCode) : null;

    // 資格名
    let certificateName = value.name ? String(value.name) : '';
    if (!certificateName && code) {
      const matched = (this.certificatesMaster || []).find((c) => String((c as any).code) === String(code));
      certificateName = matched.name || '';
    }

    // 等級名
    let gradeName = value.gradeName ? String(value.gradeName) : '';
    if (!gradeName && code && gradeCode) {
      const matched = (this.certificatesMaster || []).find(
        (c) => String((c as any).code) === String(code) && String((c as any).gradeCode) === String(gradeCode),
      );
      gradeName = matched.gradeName || '';
    }

    // 取得年月
    const issueYear = value.issueYear != null && value.issueYear !== '' ? String(value.issueYear) : '';
    const issueMonth = value.issueMonth != null && value.issueMonth !== '' ? String(value.issueMonth) : '';

    if(!certificateName) {
      return '';
    }
    
    let result = '';
    result += (certificateName && gradeName)
        ? `${certificateName} ${gradeName}\n`
        : `${certificateName}\n`;
    result += (issueYear && issueMonth)
        ? `（資格取得日: ${issueYear}年${issueMonth}月）`
        : (
          issueYear
            ? `（資格取得日: ${issueYear}年）`
            : ``
        );

    return result;

  }

  /** Dropdown1 options: 資格名のユニーク（label=name, value=code） */
  public getCertificateNameOptions(): Array<{ value: number | string; label: string }> {
    const map = new Map<string, string>(); // code -> name
    for (const c of this.certificatesMaster || []) {
      const code = String((c as any).code); // 型上 string でも number でも吸収
      if (!map.has(code)) {
        map.set(code, c.name);
      }
    }
    return this.createDropdownOptionsFrom(
      Array.from(map.entries()).map(([value, label]) => ({ value, label }))
    );
  }

  /** Dropdown2 options: 選択中の資格(code)に紐づく grade 一覧（label=gradeName, value=gradeCode） */
  public getCertificateGradeOptions(): Array<{ value: number | string; label: string }> {
    const selectedCode = this.getCertificateCode();
    if (!selectedCode) {
      return [];
    }

    const related = (this.certificatesMaster || []).filter((c) => String((c as any).code) === String(selectedCode));
    return this.createDropdownOptionsFrom(related.map(c => ({
      value: String((c as any).gradeCode),
      label: c.gradeName,
    })));
  }

  private findCertificateByCode(code: string | null): Certificate | undefined {
    if (!code) return undefined;
    return (this.certificatesMaster || []).find((c) => String((c as any).code) === String(code));
  }

  private findCertificateByCodeAndGradeCode(code: string | null, gradeCode: string | null): Certificate | undefined {
    if (!code || !gradeCode) return undefined;
    return (this.certificatesMaster || []).find(
      (c) => String((c as any).code) === String(code) && String((c as any).gradeCode) === String(gradeCode),
    );
  }
  private rebuildCertificateNameOptions(): void {
    this.certificateNameOptions = this.getCertificateNameOptions(); // 既存関数を利用でOK
    this.cdr.markForCheck();
  }
  private rebuildCertificateGradeNameOptions(): void {
    this.certificateGradeNameOptions = this.getCertificateGradeOptions(); // 既存関数を利用でOK
    this.cdr.markForCheck();
  }

  public onCertificateCodeChange(code: string | null): void {
    const current = this.valueCtrl.value || {};
    const certificate = this.findCertificateByCode(code);

    const next = {
      name: (certificate && certificate.name) || null,
      gradeName: null,
      code: code ? String(code) : null,
      gradeCode: null,
      issueYear: null,
      issueMonth: null,
    };

    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.rebuildCertificateGradeNameOptions();
    this.cdr.markForCheck();
  }

  public onCertificateGradeCodeChange(gradeCode: string | null): void {
    const current = this.valueCtrl.value || {};
    const code = this.getCertificateCode();
    const certificate = this.findCertificateByCodeAndGradeCode(code, gradeCode);

    const next = {
      ...current,
      gradeName: (certificate && certificate.gradeName) || null,
      gradeCode: gradeCode != null ? String(gradeCode) : null,
    };

    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.cdr.markForCheck();
  }

  public onCertificateIssueYearChange(issueYear: any): void {
    const current = this.valueCtrl.value || {};
    const next = {
      ...current,
      issueYear: ((issueYear && issueYear !== '') || issueYear === 0) ? Number(issueYear) : null,
    };

    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.cdr.markForCheck();
  }

  public getMonthOptions(): Array<{ value: string|number; label: string }> {
    return this.createDropdownOptionsFrom(
      Array.from({ length: 12 }, (_, i) => {
        const month = i + 1;
        return { value: month, label: `${month}月` };
      })
    );
  }

  public onCertificateIssueMonthChange(issueMonth: number | null): void {
    const current = this.valueCtrl.value || {};
    const next = {
      ...current,
      issueMonth: issueMonth != null ? Number(issueMonth) : null,
    };

    this.valueCtrl.setValue(next);
    this.valueCtrl.markAsDirty();
    this.valueCtrl.markAsTouched();
    this.cdr.markForCheck();
  }




  // -----------------------
  // UIイベント
  // -----------------------

  public onFocus(): void {
    this.onFocused.emit({ fieldName: this.fieldDefinition.fieldName });
  }

  public onBlur(): void {
    this.propagateTouched();
    this.onBlured.emit({ fieldName: this.fieldDefinition.fieldName });
  }

  // -----------------------
  // 値整形
  // -----------------------

  private prepareInitialValue(field: DynamicField, currentValue: any): any {
    if (!field) return currentValue;

    if (this.isMultilist()) {
      if (!currentValue) return [];
      if (Array.isArray(currentValue)) return currentValue;
      if (typeof currentValue === 'object') return Object.keys(currentValue);
      return [currentValue];
    }

    if (this.isIndustry()) {
      return currentValue == null || currentValue === '' ? null : String(currentValue);
    }

    if (this.isJobType()) {
      return this.normalizeJobTypeValue(currentValue)
    }

    if (this.isCertificate()) {
      if (currentValue && typeof currentValue === 'object') return currentValue;
      return {
        name: null,
        gradeName: null,
        code: null,
        gradeCode: null,
        issueYear: null,
        issueMonth: null,
      };
    }

    if (this.isDate() && currentValue) {
      const parsedMoment = moment(currentValue);
      return parsedMoment.isValid() ? parsedMoment.format('YYYY-MM-DD') : currentValue;
    }

    return currentValue || '';
  }

  private createDropdownOptionsFrom<T>(options: Array<{ value: T; label: string }>): Array<{ value: T; label: string }> {
    return [
      { value: null, label: NULL_SELECTED_VALUE },
      ...options,
    ];
  }
}
